/**
 * Find or create a RelatedPerson for an ad-hoc appointment participant (name + email).
 * Uses admin-actions `manage-eoc-party` / `create-contact-related-person` because providers
 * cannot create RelatedPerson under care-team AccessPolicy criteria (Forbidden).
 */
import type { MedplumClient } from '@medplum/core';
import type { HumanName, RelatedPerson } from '@medplum/fhirtypes';
import { config } from '@/config';
import { manageEocParty } from '@/lib/adminActionsService';

export interface AdHocAppointmentRelatedPersonInput {
  patientId: string;
  /** Optional — only used for manage-eoc-party routing metadata; not required to create. */
  episodeId?: string;
  name: string;
  email: string;
  phone?: string;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function parseAdHocParticipantName(display: string): HumanName {
  const trimmed = display.trim().replace(/\s+/g, ' ');
  const parts = trimmed.split(' ').filter(Boolean);
  if (parts.length === 0) {
    return { text: trimmed };
  }
  if (parts.length === 1) {
    return { text: trimmed, given: [parts[0]] };
  }
  return {
    text: trimmed,
    given: [parts[0]],
    family: parts.slice(1).join(' '),
  };
}

/**
 * Prefer an existing RelatedPerson on the patient chart with the same email;
 * otherwise create one via admin-actions (name + email telecom, optional phone).
 */
export async function findOrCreateAdHocAppointmentRelatedPerson(
  medplum: MedplumClient,
  input: AdHocAppointmentRelatedPersonInput,
): Promise<RelatedPerson> {
  const patientId = input.patientId.trim();
  const episodeId = input.episodeId?.trim() || '';
  const email = normalizeEmail(input.email);
  const phone = input.phone?.trim();
  const name = parseAdHocParticipantName(input.name);
  if (!patientId) {
    throw new Error('Patient is required to add a participant');
  }
  if (!name.text?.trim() && !(name.given?.[0]?.trim())) {
    throw new Error('Name is required');
  }
  if (!email || !email.includes('@')) {
    throw new Error('A valid email is required');
  }

  const result = await manageEocParty(medplum, {
    operation: 'create-contact-related-person',
    episodeId,
    primaryPatientId: patientId,
    contactName: name.text ?? [name.given?.[0], name.family].filter(Boolean).join(' '),
    firstName: name.given?.[0],
    lastName: name.family,
    email,
    phone,
    projectId: config.medplum.projectId,
  });

  if (!result.relatedPersonId) {
    throw new Error('Related person was created but no id was returned');
  }

  try {
    return await medplum.readResource('RelatedPerson', result.relatedPersonId);
  } catch {
    return {
      resourceType: 'RelatedPerson',
      id: result.relatedPersonId,
      patient: { reference: `Patient/${patientId}` },
      name: [name],
      telecom: [
        { system: 'email', value: email },
        ...(phone ? [{ system: 'phone' as const, value: phone }] : []),
      ],
    };
  }
}
